import React, { Component } from 'react';
import { Button, FormLabel } from 'react-bootstrap';

import { createMessageForError, toasterNotify } from '../../utils/toaster';
import { getFileUploadSpec } from '../../services/fileUploadsService';
import { doesFileHaveExtension } from '../../utils/assetUtils';
import { sleep } from '../../utils/serviceUtils';
import FAIcon from '../FAIcon/FAIcon';

import './AsperaFileUpload.css';

// Official IBM Aspera SDK - supports Aspera Desktop, Aspera Connect, and HTTP Gateway
import * as AsperaSDK from '@ibm-aspera/sdk';
const ASPERA_DESKTOP_DOWNLOAD_PAGE = 'https://www.ibm.com/products/aspera/downloads';

// Track initialization state across instances
let sdkInitialized = false;

function ensureSdkInitialized() {
	if (sdkInitialized) return;

	// Initialize: prefer Aspera Desktop, fall back to Aspera Connect.
	// retryTimeout: how long to wait before giving up and firing FAILED (then SDK
	// continues polling via resumePolling so a late launch still transitions to RUNNING).
	AsperaSDK.initSession({
		appId: 'pdb-client-aspera',
		retryInterval: 2000,
		retryTimeout: 3000,
		connectSettings: {
			fallback: true,
		},
	});

	sdkInitialized = true;
}



// EXPECTED PROPS:
// entityId            = The id of the entity having files uploaded for it. (eg: productId, submissionId, etc)
// entityType          = The pdb type of the entity (eg: submission, etc)
// isUploadInitiated   = Whether the parent modal has triggered the uploading to begin
// onCompleteFunction  = What to do when the service has completed what it needs to. (eg: redirect)
// updateFilesSelected = Callback executed when selected files change, with number of files passed in as first param (see: ReceiveSubmissionModal.updateFilesSelected)
// validateFileType    = OPTIONAL - Called when a file is selected, used for checking file types
// allowedFileTypes    = OPTIONAL - the FileFilters that define the allowed file types
// getToken            = OPTIONAL - Function to retrieve DA token if necessary
// singleFileOnly      = OPTIONAL - Boolean to only allow a single file to be selected.
//                                  This prop will also change allowMultipleSelection to false if set.
// disabled            = OPTIONAL - Boolean, button disabled if true

class AsperaFileUpload extends Component {
	constructor(props) {
		super(props);
		this.state = {
			entityId: this.props.entityId,
			entityType: this.props.entityType,
			isUploadInitiated: this.props.isUploadInitiated,
			selectedFiles: [],
			asperaStatus: 'INITIALIZING',
			// true while we've fired the auto-launch and are waiting for RUNNING
			autoLaunching: false,
			// dynamic installer download URL fetched from IBM's CDN on FAILED
			installerUrl: null,
			// show the Aspera Connect deprecation warning popup
			showConnectWarning: false,
		};

		this._sdkCallbackId = null;
		this._mounted = false;
		// Only auto-launch once per component instance so we don't keep pestering the OS
		this._autoLaunchFired = false;

		this.initFileSelection = this.initFileSelection.bind(this);
		this.formatFilesSelected = this.formatFilesSelected.bind(this);
		this.formatFileSelected = this.formatFileSelected.bind(this);
		this.handleFilesSelected = this.handleFilesSelected.bind(this);
		this.runAsperaUpload = this.runAsperaUpload.bind(this);
		this.startFileUpload = this.startFileUpload.bind(this);
		this.removeFile = this.removeFile.bind(this);
		this.validateFileType = this.validateFileType.bind(this);
		this.launchAspera = this.launchAspera.bind(this);
	}

	componentDidMount() {
		this._mounted = true;

		// Register directly with the SDK — it fires the current status immediately,
		// so every new instance (e.g. reopened modal) gets the right status right away.
		this._sdkCallbackId = AsperaSDK.registerStatusCallback((status) => {
			// eslint-disable-next-line no-console
			console.log('[AsperaSDK] Status:', status);
			if (!this._mounted) return;

			this.setState({ asperaStatus: status });

			// When Aspera isn't running yet, try to launch it automatically on the
			// first FAILED event.  window.open with aspera:// is the most reliable
			// cross-browser way — it's visible in dev-tools Network/Console and works
			// in all major browsers without being silently swallowed.
			// The SDK's resumePolling keeps running in the background, so if Aspera
			// starts the status will flip to RUNNING on its own.
			if (status === 'FAILED' && !this._autoLaunchFired) {
				this._autoLaunchFired = true;
				// eslint-disable-next-line no-console
				console.log('[AsperaSDK] Not detected — firing AsperaSDK.launch() (hidden iframe, no popup blocker). SDK polls every 2s and will flip to RUNNING once Aspera starts.');
				// eslint-disable-next-line no-console
				console.log('[AsperaSDK] TIP: In DevTools → Network (WS filter), watch for ws://127.0.0.1:33024 — failed = Aspera not running, success = RUNNING incoming.');
				this.setState({ autoLaunching: true });
				// Fetch the platform-specific installer URL from IBM's CDN so the
				// "Install" link points directly to the right download.
				this._fetchInstallerUrl();
				// AsperaSDK.launch() injects a hidden <iframe src="aspera://"> into the page —
				// not subject to popup blockers. macOS will show "Open IBM Aspera Desktop?" once.
				this._triggerAsperaLaunch();
				// Clear the "Starting…" indicator after a few seconds regardless;
				// the RUNNING status callback will also clear it if Aspera starts.
				if (this._autoLaunchTimer) clearTimeout(this._autoLaunchTimer);
				this._autoLaunchTimer = setTimeout(() => {
					if (this._mounted) {
						this.setState({ autoLaunching: false });
						// eslint-disable-next-line no-console
						console.log('[AsperaSDK] Auto-launch window expired. SDK still polling — status will flip to RUNNING automatically if Aspera Desktop starts.');
					}
				}, 5000);
			}

			if (status === 'RUNNING') {
				this.setState({ autoLaunching: false });
			}
		});

		ensureSdkInitialized();

		// Fetch the installer URL eagerly so it's available for the Connect warning
		// popup even when Aspera is already RUNNING (i.e. _fetchInstallerUrl won't
		// be triggered by the FAILED path).
		this._fetchInstallerUrl();
	}

	componentWillUnmount() {
		this._mounted = false;
		if (this._sdkCallbackId) {
			AsperaSDK.deregisterStatusCallback(this._sdkCallbackId);
			this._sdkCallbackId = null;
		}
		if (this._autoLaunchTimer) {
			clearTimeout(this._autoLaunchTimer);
		}
	}

	/**
	 * Fetch the platform-specific installer download URL from IBM's CDN using
	 * AsperaSDK.getInstallerInfo() and store it in state.
	 */
	async _fetchInstallerUrl() {
		try {
			const info = await AsperaSDK.getInstallerInfo();
			// info.entries is already filtered to the current platform.
			const entry = info && info.entries && info.entries[0];
			if (entry && entry.url && this._mounted) {
				this.setState({ installerUrl: entry.url });
			}
		} catch (err) {
			// eslint-disable-next-line no-console
			console.warn('[AsperaSDK] Could not fetch installer info:', err);
		}
	}

	/**
	 * Fire the aspera:// URI scheme via AsperaSDK.launch() (hidden iframe).
	 * The iframe approach avoids the OS "Open in app?" confirmation dialog that
	 * window.open() triggers. Safe to call from both user gestures and callbacks.
	 */
	_triggerAsperaLaunch() {
		// eslint-disable-next-line no-console
		console.log('[AsperaSDK] Firing launch() — watch DevTools Elements for hidden <iframe src="aspera://"> on <body>.');
		AsperaSDK.launch();
	}

	/** Called by the manual "Start Aspera Desktop" link. */
	launchAspera() {
		this.setState({ autoLaunching: true });
		this._triggerAsperaLaunch();
		// Clear the "Starting…" indicator after a few seconds;
		// status callback will flip autoLaunching off on RUNNING.
		if (this._autoLaunchTimer) clearTimeout(this._autoLaunchTimer);
		this._autoLaunchTimer = setTimeout(() => {
			if (this._mounted) this.setState({ autoLaunching: false });
		}, 5000);
	}

	async initFileSelection() {
		const { allowedFileTypes, singleFileOnly } = this.props;
		const { asperaStatus } = this.state;

		if (asperaStatus !== 'RUNNING') {
			toasterNotify(
				'Aspera is not ready (' + asperaStatus + '). Please wait for Aspera Desktop or Aspera Connect to connect.',
				'warning'
			);
			return;
		}

		try {
			const isConnect = AsperaSDK.currentTransferClient() === 'connect';
			const dialogOptions = isConnect
				? {
					// Aspera Connect (AW4) keys
					allowMultipleSelection: !singleFileOnly,
					allowedFileTypes: allowedFileTypes
						? allowedFileTypes.map(ft => ({ filter: ft.filter_name, extensions: ft.extensions }))
						: undefined,
				}
				: {
					// Aspera Desktop (IBM SDK) keys
					multiple: !singleFileOnly,
					filters: allowedFileTypes
						? allowedFileTypes.map(ft => ({ name: ft.filter_name, extensions: ft.extensions }))
						: undefined,
				};
			const result = await AsperaSDK.showSelectFileDialog(dialogOptions);

			const fileUploads = result && result.dataTransfer && result.dataTransfer.files;
			if (fileUploads && fileUploads.length > 0) {
				let formattedFiles = this.formatFilesSelected(fileUploads).filter(
					(file) => file.file_name?.length <= 200,
				);
				if (fileUploads.length > formattedFiles.length) {
					toasterNotify(
						'Files with names over 200 characters (extensions included) cannot be processed and have been left off the selection.',
						'error',
					);
				}
				if (formattedFiles.length > 0) {
					this.handleFilesSelected(formattedFiles);
				}
			}
		} catch (err) {
			// The SDK rejects when the user cancels/dismisses the dialog, switches
			// windows (causing a timeout), or loses focus. All of these are
			// user-initiated no-ops — never show an error toast for a dialog
			// rejection. There is no actionable "real error" that can come from
			// showSelectFileDialog; any rejection simply means no file was chosen.
			// eslint-disable-next-line no-console
			console.log('[AsperaSDK] showSelectFileDialog dismissed/cancelled:', err);
		}
	}

	formatFilesSelected(fileUploads) {
		let files = [];
		fileUploads.forEach((upload) => {
			files.push(this.formatFileSelected(upload));
		});
		return files;
	}

	formatFileSelected(upload) {
		let filePath = upload.name.split(/\\|\//);
		let fileName = filePath[filePath.length - 1];

		return {
			file_name: fileName,
			file_path: upload.name,
			content_type: upload.type
		};
	}

	handleFilesSelected(newFiles) {
		const { selectedFiles } = this.state;
		const { singleFileOnly } = this.props;
		if (singleFileOnly) {
			selectedFiles.length = 0;
			selectedFiles.push(newFiles[0]);
		} else {
			newFiles.forEach((upload) => {
				selectedFiles.push(upload);
			});
		}
		this.setState(
			{
				selectedFiles: selectedFiles
			},
			() => {
				this.validateFileType();
			}
		);
		this.props.updateFilesSelected(true);
	}

	removeFile(fileName) {
		const { selectedFiles } = this.state;
		if (this.props.disabled) {
			return;
		}
		// TODO: Not sure this accounts for same name files in diff paths
		const filteredFiles = selectedFiles.filter(
			(file) => file.file_name !== fileName
		);
		this.setState(
			{
				selectedFiles: filteredFiles
			},
			() => {
				this.validateFileType();
			}
		);
		this.props.updateFilesSelected(filteredFiles.length > 0);
	}

	validateFileType() {
		const { validateFileType } = this.props;
		const { selectedFiles } = this.state;
		if (validateFileType) {
			validateFileType([...selectedFiles]);
		}
	}

	async initiateTransfer() {
		const { onCompleteFunction, getToken, prefetchedTransferSpecs } = this.props;
		const { selectedFiles, entityType, entityId } = this.state;


		try {
			let transferSpecs = prefetchedTransferSpecs;
			if (!transferSpecs) {
				const fileUploadPayload = {
					entity_type: entityType,
					entity_id: entityId,
					files: selectedFiles
				};
				const token = (getToken ? await getToken() : undefined);
				const response = await getFileUploadSpec(fileUploadPayload, token);
				transferSpecs = response.data;
			} else {
				// this is a hack to get the callback in setState() to execute
				await sleep(1);
			}
			this.runAsperaUpload(transferSpecs);
		} catch (err) {
			toasterNotify(
				createMessageForError(err, 'retrieving upload specs'),
				'error',
				err
			);
		} finally {
			if (onCompleteFunction != null) {
				onCompleteFunction();
			}
		}
	}

	runAsperaUpload(uploadSpec) {
		uploadSpec.transfer_specs.forEach((specification) => {
			if (!specification) {
				return;
			}
			this.startFileUpload(specification.transfer_spec);
		});
	}

	async startFileUpload(spec) {
		if (!spec || spec.error) {
			// eslint-disable-next-line no-console
			console.error('[AsperaSDK] Invalid transfer spec:', spec);
			toasterNotify('File upload error', 'error');
			return;
		}

		spec.create_dir = true;
		spec.authentication = 'token';

		const fileName = this.getFileName(spec);

		try {
			await AsperaSDK.startTransfer(spec, { allow_dialogs: false });
			toasterNotify('Uploading ' + fileName + '...', 'info');
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('[AsperaSDK] Transfer error:', err);
			toasterNotify('File upload error on ' + fileName, 'error');
		}
	}

	getFileName(spec) {
		let fileName = spec.paths[0].destination;
		if (fileName.length > 20) {
			fileName = fileName.slice(0, 20);
		}
		return fileName;
	}

	componentDidUpdate(prevProps) {
		if (this.props.isUploadInitiated && !prevProps.isUploadInitiated) {
			this.setState(
				{
					isUploadInitiated: this.props.isUploadInitiated
				},
				() => {
					this.initiateTransfer();
				}
			);
		}
		if (this.props.entityId && prevProps.entityId !== this.props.entityId) {
			this.setState({ entityId: this.props.entityId });
		}
	}

	render() {
		const { allowedFileTypes, singleFileOnly, disabled } = this.props;
		const { selectedFiles, asperaStatus, autoLaunching, installerUrl, showConnectWarning } = this.state;

		const isReady = asperaStatus === 'RUNNING';

		// Show a status suffix in the button only while initializing/starting — not for
		// error states that already have a message below the button.
		const buttonStatusSuffix = autoLaunching
			? ' (Starting Aspera…)'
			: asperaStatus === 'INITIALIZING'
				? ' - initializing Aspera'
				: '';

		const transferClient = isReady ? AsperaSDK.currentTransferClient() : null;
		const clientBadge = transferClient ? (
			<span className="AsperaFileUpload__client-badge" style={{ verticalAlign: 'middle' }}>
				{transferClient === 'connect' ? (
					<>
						<button
							type="button"
							className="AsperaFileUpload__connect-warning-link"
							onClick={() => this.setState({ showConnectWarning: true })}
						>
							Aspera Connect?
						</button>
						{showConnectWarning && (
							<div
								className="AsperaFileUpload__connect-warning-overlay"
								onClick={() => this.setState({ showConnectWarning: false })}
							>
								<div
									className="AsperaFileUpload__connect-warning-popup"
									role="dialog"
									aria-modal="true"
									aria-label="Aspera Connect deprecation notice"
									tabIndex={-1}
									onClick={(e) => e.stopPropagation()}
								>
									<button
										type="button"
										className="AsperaFileUpload__connect-warning-close"
										onClick={() => this.setState({ showConnectWarning: false })}
										aria-label="Close"
									>
										&times;
									</button>
									<p>
										<strong>Aspera Connect detected!</strong>
									</p>
									<p>
										Support to upload files in the Nintendo Publisher Tool using IBM Aspera Connect will end on{' '}
										<strong>December 31st, 2026</strong>. Please install and use{' '}
										<a
											href={installerUrl || ASPERA_DESKTOP_DOWNLOAD_PAGE}
											target="_blank"
											rel="noopener noreferrer"
										>
											IBM Aspera Desktop
										</a>{' '}
										prior to December 31st.
									</p>
								</div>
							</div>
						)}
					</>
				) : null}
			</span>
		) : null;

		// For INITIALIZING and autoLaunching, status is shown in the button label — no separate message needed.
		let asperaMessage = null;
		let asperaSubMessage = null;
		if (asperaStatus === 'FAILED' && !autoLaunching) {
			asperaMessage = (
				<span className="AsperaFileUpload__status text-danger">
					Aspera not detected.
				</span>
			);
			asperaSubMessage = (
				<div className="AsperaFileUpload__install-link">
					<div>
						{/* Opens aspera:// in a short-lived window — visible in DevTools,
						    works in all browsers. SDK keeps polling so RUNNING fires
						    automatically once the app is up. */}
						<a
							href="aspera://"
							className="AsperaFileUpload__wake-link"
							onClick={(e) => { e.preventDefault(); this.launchAspera(); }}
						>
							Start IBM Aspera Desktop
						</a>
					</div>
					<div className="AsperaFileUpload__or-divider">-or-</div>
					<div>
						<a href={installerUrl || ASPERA_DESKTOP_DOWNLOAD_PAGE} target="_blank" rel="noopener noreferrer">
							Install IBM Aspera Desktop
						</a>
					</div>
				</div>
			);
		} else if (asperaStatus === 'EXTENSION_INSTALL') {
			asperaMessage = (
				<span className="AsperaFileUpload__status text-warning">
					Browser extension required.{' '}
					<a href="https://www.ibm.com/aspera/connect/" target="_blank" rel="noopener noreferrer">
						Install Aspera Connect
					</a>
				</span>
			);
		} else if (asperaStatus === 'OUTDATED') {
			asperaMessage = (
				<span className="AsperaFileUpload__status text-warning">
					Aspera Connect is out of date.{' '}
					<a href="https://www.ibm.com/aspera/connect/" target="_blank" rel="noopener noreferrer">
						Update
					</a>
				</span>
			);
		}

		return (
			<div>
				<div className="AsperaFileUpload__select-row">
					<input
						type="button"
						value={(!singleFileOnly ? 'Select files...' : 'Select file...') + buttonStatusSuffix}
						onClick={this.initFileSelection}
						disabled={disabled || !isReady}
						style={{ verticalAlign: 'middle' }}
					/>
					{clientBadge}
					{asperaMessage}
				</div>
				{asperaSubMessage}
				{selectedFiles && selectedFiles.length > 0 && (
					<div className={disabled ? 'AsperaFileUpload__disabled' : undefined}>
						{selectedFiles.map((file, index) => {
							const wrongType = allowedFileTypes && !allowedFileTypes.find(type =>
								doesFileHaveExtension(file.file_name, type.extensions)
							);
							return (
								<div key={index} style={{'wordBreak': 'break-word'}}>
									<FormLabel
										className={wrongType ? 'text-danger' : ''}
										id={file.file_name + '_control_label'}
										key={index + '_control_label'}
									>
										{file.file_name}
									<Button
											variant="plain"
											className="AsperaFileUpload__remove-file ml-1"
											id={file.file_name + '_button'}
											key={index + '_button'}
											aria-label="Remove file"
											onClick={() => {
												this.removeFile(file.file_name);
											}}
											disabled={this.props.disabled}
										>
											<FAIcon name="times-square" className={!this.props.disabled && 'text-danger'} />
										</Button>
									</FormLabel>

								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	}
}

export default AsperaFileUpload;
